/**
 * Quais atividades o aluno ainda TEM que fazer — a conta por trás do número no
 * menu lateral.
 *
 * O badge de "Atividades" na `Sidebar` contava `activities.length` do endpoint
 * `general/dashboard/user`, e esse endpoint devolve `Activity::filter` com
 * `todo = null` e `late = null`: TODAS as alocações do ano do aluno, incluindo
 * as que ele já entregou e as que já encerraram. O aluno terminava tudo e o
 * menu continuava marcando dezenas de atividades.
 *
 * Uma atividade só entra na conta quando as três coisas valem ao mesmo tempo:
 *
 *  1. não está concluída — `activity_percentage` (que é
 *     `activity_executions.completion_percentage`) abaixo de 100;
 *  2. o prazo não passou — `activity_end` no futuro;
 *  3. já está liberada — `activity_start` no passado, ou `activity_release`
 *     ligado, que é a liberação manual do professor.
 *
 * As regras 1 e 2 são as mesmas de `activities_pending` nos dashboards do aluno
 * (`LegacyStudentDashboard`), para o menu não discordar do painel que o aluno vê
 * na mesma tela. A regra 3 é a da página de execução (`Activity/Do`), que
 * responde "A atividade ainda não foi liberada" nesse caso: contar uma
 * atividade que só abre na semana que vem manda o aluno para uma tela fechada.
 *
 * O que NÃO é filtrado aqui, de propósito: tipo de atividade. Uma aula ao vivo
 * (`type_id === 2`) nunca ganha `completion_percentage`, então ela conta
 * enquanto a janela dela estiver aberta — que é exatamente quando o aluno pode
 * entrar. Assim que a janela fecha, sai da conta pela regra 2.
 */

/**
 * A data como `Date`, ou `null` quando não dá para saber.
 *
 * O backend manda `"2026-08-12 23:59:59"`, com espaço em vez de "T". O
 * `new Date` do Chrome aceita, mas o de Safari/iOS devolve `Invalid Date` — e
 * `new Date("...") < new Date()` com data inválida é `false`, o que faria toda
 * atividade parecer dentro do prazo num navegador e fora no outro. Trocar o
 * espaço por "T" dá o mesmo instante nos dois (hora local, sem fuso no texto).
 */
const paraData = valor => {
  if (!valor) return null;
  if (valor instanceof Date)
    return Number.isNaN(valor.getTime()) ? null : valor;
  if (typeof valor !== "string") return null;

  const data = new Date(valor.trim().replace(" ", "T"));
  return Number.isNaN(data.getTime()) ? null : data;
};

/**
 * Já entregue? `null`/`undefined` é atividade nunca aberta, então não.
 *
 * EXPORTADA de propósito, e não só usada aqui dentro: o dashboard do aluno
 * precisa da mesma resposta para marcar o cartão de concluída, e escrever
 * `activity_percentage >= 100` lá seria a segunda definição do mesmo conceito —
 * duas réguas que envelhecem separadas, com o sintoma clássico de o menu
 * lateral não contar uma atividade que o cartão ao lado mostra sem selo.
 *
 * O `Number` NÃO muda resultado nenhum, e vale saber disso antes de "limpar" o
 * código: parte do acervo manda o percentual como STRING (`"100"`), mas `>=`
 * com um número do outro lado já coage — medido: `"40" >= 100` é `false`,
 * `"100" >= 100` é `true`, e `null`/`undefined` dão `false`. O cast está aqui
 * como declaração de intenção e como seguro contra a mudança que estragaria a
 * conta de verdade: comparar com `"100"`, string dos dois lados, onde
 * `"40" >= "100"` vira `true` porque passa a ser ordem alfabética.
 *
 * @param {object} atividade Item de `data.activities` de `general/dashboard/user`.
 * @returns {boolean}
 */
export const estaConcluida = atividade =>
  Number(atividade?.activity_percentage) >= 100;

/**
 * O prazo já passou?
 *
 * Sem `activity_end` — ou com data que não analisa — a resposta é não: alocação
 * sem prazo é atividade que segue aberta, e sumir com ela do menu por causa de
 * um campo vazio esconde trabalho de verdade.
 */
const prazoEncerrado = (atividade, agora) => {
  const fim = paraData(atividade.activity_end);
  return fim !== null && fim < agora;
};

/**
 * Ainda não abriu?
 *
 * `activity_release` é a liberação manual do professor (booleano em
 * `activity_allocations`) e vence a data de início, igual na página de execução.
 */
const aindaNaoLiberada = (atividade, agora) => {
  if (atividade.activity_release) return false;

  const inicio = paraData(atividade.activity_start);
  return inicio !== null && inicio > agora;
};

/**
 * A atividade está pendente para o aluno neste momento?
 *
 * @param {object} atividade Item de `data.activities` de `general/dashboard/user`.
 * @param {Date} agora Instante de referência — injetável para os testes.
 * @returns {boolean}
 */
export const estaPendente = (atividade, agora = new Date()) =>
  !!atividade &&
  !estaConcluida(atividade) &&
  !prazoEncerrado(atividade, agora) &&
  !aindaNaoLiberada(atividade, agora);

/**
 * Só as atividades que o aluno ainda pode fazer, na ordem em que vieram.
 *
 * A ordem importa: quem chama usa a primeira da lista para montar o link do
 * menu, e a API já devolve ordenado por `activity_allocations.start` desc.
 *
 * @param {Array} atividades
 * @param {Date} agora
 * @returns {Array}
 */
export const filtrarPendentes = (atividades, agora = new Date()) =>
  Array.isArray(atividades)
    ? atividades.filter(atividade => estaPendente(atividade, agora))
    : [];

/**
 * Quantas atividades entram no badge. Zero quando a lista não veio.
 *
 * @param {Array} atividades
 * @param {Date} agora
 * @returns {number}
 */
export const contarPendentes = (atividades, agora = new Date()) =>
  filtrarPendentes(atividades, agora).length;
